/*
  The cart: the drawer and the cart page.

  Both show the cart-panel snippet and change the cart the same way, so
  the logic lives once, in CartBase, and the two elements add only what is
  their own:

    <cart-drawer>  opening, closing, the scroll lock, and the count on the
                   bag. It keeps Dawn's name and its renderContents and
                   getSectionsToRender methods, because the product page,
                   its sticky bar, Complete the look, quick add and the
                   cart's "+ Add" rows all look for a <cart-drawer> and call
                   those after adding to the cart.
    <cart-page>    redraws itself, and the drawer behind it, in place.

  What changes the cart from here: a quantity tap or remove, and a new
  colour or size picked on a row. Both go to /cart/update. cart-panel.js
  adds the instant heading count and Mix & Match strip on top.

  Loaded by both sections, so on /cart it arrives twice; each element is
  defined only once.
*/
(() => {
  if (customElements.get('cart-drawer')) return;

  // How long a refused change stays explained on screen.
  const NOTICE_MS = 6000;

  // The totals while the answer is on its way: dimmed, together, so they
  // read as about to change rather than as final.
  const LOADING_OPACITY = '0.4';

  // The smallest the totals are shrunk to on a narrow screen; see fitTotals.
  const TOTALS_MIN_PX = 11;

  /*
    Writes a number of cents in the same format as an amount already on the
    page, so a preview reads exactly like the figure it replaces: the same
    currency, the same decimal mark, the same place for the symbol.
  */
  function formatLike(sample, cents) {
    const match = sample.match(/\d[\d'’.,\s]*\d|\d/);
    if (!match) return sample;

    const number = match[0];
    const decimals = number.match(/[.,]\d{2}$/);
    const decimalMark = decimals ? decimals[0][0] : null;
    const wholePart = decimalMark ? number.slice(0, -3) : number;
    const groupMatch = wholePart.match(/[^\d]/);
    const groupMark = groupMatch ? groupMatch[0] : decimalMark === ',' ? '.' : ',';

    const value = Math.max(0, Math.round(cents));
    const whole = String(Math.floor(value / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, groupMark);
    const fraction = String(value % 100).padStart(2, '0');

    return sample.replace(number, decimalMark ? `${whole}${decimalMark}${fraction}` : whole);
  }

  /*
    The keys of the cart lines a row stands for. Usually one; more when
    Shopify split the variant over several lines for a discount and the
    snippet added them up into one row.
  */
  function rowKeys(row) {
    return row ? (row.dataset.keys || '').split(',').filter(Boolean) : [];
  }

  class CartBase extends HTMLElement {
    connectedCallback() {
      /*
        Where every change is sent, from Shopify's own routes, written onto
        the element by the section.
      */
      this.updateUrl = `${this.dataset.updateUrl || '/cart/update'}.js`;

      // How many changes are in flight, and what they have left behind for
      // the last one to come back; see request.
      this.pending = 0;
      this.latestState = null;
      this.needsRefresh = false;
      this.pendingNotice = null;
      this.noticeTimer = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
    }

    onClick(event) {
      const button = event.target.closest('[data-cart-qty]');
      if (button && !button.disabled) this.changeQuantity(button);
    }

    onChange(event) {
      const select = event.target.closest('[data-cart-variant]');
      if (select) this.changeVariant(select);
    }

    /* Changes ----------------------------------------------------------- */

    /*
      What belongs to the row changes on the tap, and the request goes out
      behind it: the number, the row when it goes to zero, the row price,
      and the counts. The totals are dimmed instead of previewed, and come
      back with the answer: they depend on discounts only the server works
      out.

      The quantity is read from what is on screen, so two quick taps count
      1, 2, 3 instead of both sending the same number.

      A row can stand for several lines of one variant: the new total goes
      on the first and the others are set to zero, in one request. Remove
      is the same with a quantity of zero.
    */
    changeQuantity(button) {
      const row = button.closest('.cart-item');
      const keys = rowKeys(row);
      if (!keys.length) return;

      const display = row.querySelector('.cart-qty__value');
      const removing = button.dataset.cartQty === '0';
      const before = display ? parseInt(display.textContent, 10) || 0 : 0;

      let quantity = 0;
      if (!removing && display) {
        const step = button.matches('.cart-qty__button:last-of-type') ? 1 : -1;
        quantity = Math.max(0, before + step);
        display.textContent = quantity;
      } else if (!removing) {
        quantity = Number(button.dataset.cartQty);
      }

      if (quantity === 0) row.style.display = 'none';

      this.previewLine(row, before, quantity);
      this.syncCount();
      this.setTotalsLoading();

      const updates = {};
      keys.forEach((key, index) => {
        updates[key] = index === 0 ? quantity : 0;
      });

      this.request({ updates });
    }

    /*
      A new colour or size picked on a row. Shopify cannot change a line's
      variant in place, so the row's lines are set to zero and the new
      variant added at the row's quantity, in one request.
    */
    changeVariant(select) {
      const row = select.closest('.cart-item');
      const keys = rowKeys(row);
      if (!keys.length || !select.value) return;

      const display = row.querySelector('.cart-qty__value');
      const quantity = display ? parseInt(display.textContent, 10) || 1 : 1;

      const value = row.querySelector('.cart-item__variant-value');
      const option = select.options[select.selectedIndex];
      if (value && option) value.textContent = option.textContent.trim();
      row.setAttribute('aria-busy', 'true');

      this.setTotalsLoading();

      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });
      updates[select.value] = quantity;

      this.request({ updates });
    }

    /*
      Sends a change and, once every change in flight is back, redraws.

      Only the last request to come back redraws, so an earlier answer
      arriving late cannot put an older cart back on screen. The count of
      requests out is lowered in exactly one place, the finally block, once
      per request whatever happened to it. When the last is back, the cart
      is always redrawn: from the most recent answer, or fetched afresh if
      any request was refused or failed, with Shopify's reason shown above
      the total when it gave one.
    */
    async request(payload) {
      this.pending += 1;

      let state = null;
      let failed = false;

      try {
        const response = await fetch(this.updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            ...payload,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });

        state = await response.json();
        failed = !response.ok || Boolean(state.status);
      } catch (error) {
        failed = true;
      } finally {
        this.pending -= 1;
      }

      if (failed) {
        this.needsRefresh = true;
        if (state) this.pendingNotice = state.description || '';
      } else {
        this.latestState = state;
      }

      if (this.pending > 0) return;

      const latest = this.latestState;
      const refresh = this.needsRefresh;
      const notice = this.pendingNotice;

      this.latestState = null;
      this.needsRefresh = false;
      this.pendingNotice = null;

      if (refresh || !latest) {
        await this.refresh();
      } else {
        this.renderContents(latest);
      }

      if (notice !== null) this.showNotice(notice);
    }

    /* The instant preview of a quantity change on its own row. */
    previewLine(row, before, after) {
      if (!row || before === after) return;

      const unit = Number(row.dataset.unitPrice);
      if (!Number.isFinite(unit) || row.dataset.unitPrice === '') return;

      const unitWas = Number(row.dataset.unitWas);

      const price = row.querySelector('.cart-item__price-final') || row.querySelector('.cart-item__price > span');
      if (price) price.textContent = formatLike(price.textContent, unit * after);

      const was = row.querySelector('.cart-item__price-was');
      if (was && Number.isFinite(unitWas)) was.textContent = formatLike(was.textContent, unitWas * after);
    }

    /* Dims the totals together, until the redraw replaces them. */
    setTotalsLoading() {
      const totals = this.querySelector('.cart-total__values');
      if (!totals) return;

      totals.style.transition = 'opacity 0.15s ease';
      totals.style.opacity = LOADING_OPACITY;
    }

    /*
      Keeps the totals row on one line: when a large order needs more room
      than a phone has, the amounts shrink together, half a pixel at a time,
      until the row fits. Starts from the stylesheet's own size every time.
    */
    fitTotals() {
      const row = this.querySelector('.cart-total');
      const values = row ? row.querySelector('.cart-total__values') : null;
      if (!values) return;

      values.style.fontSize = '';
      if (row.clientWidth === 0) return;

      let size = parseFloat(window.getComputedStyle(values).fontSize) || 14;

      while (row.scrollWidth > row.clientWidth && size > TOTALS_MIN_PX) {
        size = Math.max(TOTALS_MIN_PX, size - 0.5);
        values.style.fontSize = `${size}px`;
      }
    }

    /* Redraws from the cart as it really is, for when an answer failed. */
    async refresh() {
      try {
        const ids = this.getSectionsToRender().map((section) => section.id).join(',');
        const response = await fetch(`${window.location.pathname}?sections=${ids}`, {
          headers: { Accept: 'application/json' },
        });
        const sections = await response.json();

        this.renderContents({ sections });
      } catch (error) {
        window.location.href = window.routes.cart_url;
      }
    }

    /*
      A short line above the total, saying why a change did not happen:
      Shopify's own reason, or the fallback from Theme settings.
    */
    showNotice(message) {
      const text = message || this.dataset.cartError;
      if (!text) return;

      const foot = this.querySelector('.cart-drawer__foot');
      if (!foot) return;

      let notice = this.querySelector('[data-cart-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.className = 'cart-drawer__notice';
        notice.setAttribute('role', 'alert');
        notice.setAttribute('data-cart-notice', '');
        foot.prepend(notice);
      }

      notice.textContent = text;

      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => notice.remove(), NOTICE_MS);
    }

    /* Rendering --------------------------------------------------------- */

    // Puts the new markup in place for every section asked for.
    replaceSections(state) {
      this.getSectionsToRender().forEach((section) => {
        const markup = state.sections[section.id];
        if (!markup) return;

        const target = document.querySelector(section.selector);
        const source = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector(section.selector);

        if (target && source) target.innerHTML = source.innerHTML;
      });

      this.syncCount();
    }

    /*
      The count on the bag in the header, from the quantities on screen.
      Rows hidden by a removal still on its way are left out, so the count
      changes on the tap.
    */
    syncCount() {
      let count = 0;
      this.querySelectorAll('.cart-item').forEach((row) => {
        if (row.style.display === 'none') return;
        const value = row.querySelector('.cart-qty__value');
        if (value) count += parseInt(value.textContent, 10) || 0;
      });

      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;

      let dot = bubble.querySelector('.site-header__cart-count');

      if (count > 0) {
        if (!dot) {
          dot = document.createElement('span');
          dot.className = 'site-header__cart-count';
          bubble.appendChild(dot);
        }
        dot.textContent = count;
      } else if (dot) {
        dot.remove();
      }
    }
  }

  /* Drawer ------------------------------------------------------------- */

  // Must match the closing transition in cart-panel.css.
  const CLOSE_MS = 350;

  // The part of the drawer that scrolls up and down. Every other swipe in
  // the drawer is cancelled.
  const VERTICAL_SCROLLER = '.cart-drawer__body';

  class CartDrawer extends CartBase {
    connectedCallback() {
      super.connectedCallback();

      this.closeTimer = null;
      this.scrollLocked = false;
      this.lockedScrollY = 0;
      this.touchStartY = 0;

      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);

      /*
        The header cart link is a plain anchor to /cart, so the drawer
        intercepts it. Left as a real link: with JavaScript off it still goes
        to the cart page. On the cart page itself it is left alone, since the
        cart is already on screen.
      */
      const trigger = document.getElementById('cart-icon-bubble');
      if (trigger && !document.querySelector('cart-page')) {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
        });
      }

      window.addEventListener('resize', () => {
        if (!this.hidden) this.fitTotals();
      });

      this.syncCount();
    }

    onClick(event) {
      if (event.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }
      super.onClick(event);
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '[data-cart-inner]' }];
    }

    // Called by the product page and the extras after adding, as well as by
    // every change made in the drawer itself.
    renderContents(state) {
      if (state && state.sections) this.replaceSections(state);
      this.open();
    }

    open() {
      clearTimeout(this.closeTimer);

      this.hidden = false;
      this.lockScroll();

      // Measured now that the drawer is visible, before the slide starts.
      this.fitTotals();

      // A frame between being rendered and being animated, or the browser
      // has nothing to transition from.
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('cart-open');
      });

      document.addEventListener('keydown', this.onKeydown);

      const close = this.querySelector('.cart-drawer__close');
      if (close) close.focus({ preventScroll: true });
    }

    close() {
      if (this.hidden) return;

      this.classList.remove('is-open');
      document.body.classList.remove('cart-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.unlockScroll();

      this.closeTimer = setTimeout(() => {
        this.hidden = true;
      }, CLOSE_MS);
    }

    /*
      The page behind the drawer is held still: the body is pinned in place,
      the page's elastic overscroll is switched off, and every swipe inside
      the drawer that is not scrolling its list is cancelled. Guarded,
      because every redraw calls open() again.
    */
    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;

      this.lockedScrollY = window.scrollY || window.pageYOffset || 0;

      const body = document.body.style;
      body.position = 'fixed';
      body.top = `-${this.lockedScrollY}px`;
      body.left = '0';
      body.right = '0';
      body.width = '100%';

      document.documentElement.classList.add('cart-locked');

      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { passive: false });
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;

      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchmove', this.onTouchMove);

      document.documentElement.classList.remove('cart-locked');

      const body = document.body.style;
      body.position = '';
      body.top = '';
      body.left = '';
      body.right = '';
      body.width = '';

      // instant: the theme's smooth scrolling would glide the page back.
      window.scrollTo({ top: this.lockedScrollY, left: 0, behavior: 'instant' });
    }

    onTouchStart(event) {
      if (event.touches.length !== 1) return;
      this.touchStartY = event.touches[0].clientY;
    }

    onTouchMove(event) {
      if (event.touches.length !== 1 || !event.cancelable) return;

      const dy = event.touches[0].clientY - this.touchStartY;

      const scroller = event.target.closest(VERTICAL_SCROLLER);
      if (!scroller) {
        event.preventDefault();
        return;
      }

      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

      if ((dy > 0 && atTop) || (dy < 0 && atBottom)) event.preventDefault();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }
  }

  /* Cart page ---------------------------------------------------------- */

  /*
    Redraws itself in place after a change, and the drawer behind it too,
    so the drawer is already up to date should it be opened from here.
  */
  class CartPage extends CartBase {
    connectedCallback() {
      super.connectedCallback();

      window.addEventListener('resize', () => this.fitTotals());

      this.fitTotals();
    }

    getSectionsToRender() {
      return [
        { id: this.dataset.sectionId, selector: '[data-cart-page-inner]' },
        { id: 'cart-drawer', selector: '[data-cart-inner]' },
      ];
    }

    renderContents(state) {
      if (state && state.sections) this.replaceSections(state);
      this.fitTotals();
    }
  }

  customElements.define('cart-drawer', CartDrawer);
  customElements.define('cart-page', CartPage);
})();
